import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ClientsToolbar from './ClientsToolbar';
import ClientsTable from './ClientsTable';
import ClientShow from './ClientShow';

const PER_PAGE_DEFAULT = 30;

export default function ClientsIndex({ active, plans, current_url }) {
  const csrfToken = typeof document !== 'undefined'
    ? document.querySelector("[name='csrf-token']")?.content
    : '';

  const [planId, setPlanId]     = useState(plans?.[0]?.id ?? null);
  const [page, setPage]         = useState(1);
  const [perPage]               = useState(PER_PAGE_DEFAULT);
  const [sortField, setSortField] = useState('id');
  const [sortDir, setSortDir]   = useState('DESC');
  const [q, setQ]               = useState('');
  const [appliedQ, setAppliedQ] = useState('');

  const [rows, setRows]         = useState([]);
  const [meta, setMeta]         = useState({ total: 0, total_pages: 0 });
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [selectedClientId, setSelectedClientId] = useState(null);

  const fetchList = useCallback(async () => {
    if (!planId) return;
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({
      page,
      per_page: perPage,
      sort_field: sortField,
      sort_dir: sortDir,
      plan_id: planId,
      active: String(active),
    });
    if (appliedQ) params.append('q', appliedQ);

    try {
      const res = await fetch(`/settings/clients_v2/list.json?${params.toString()}`, {
        headers: { 'X-CSRF-Token': csrfToken, Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setRows(json.data || []);
      setMeta(json.meta || {});
    } catch (e) {
      setError(e.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [planId, page, perPage, sortField, sortDir, appliedQ, active, csrfToken]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const onSort = useCallback((field) => {
    if (sortField === field) {
      setSortDir(d => d === 'DESC' ? 'ASC' : 'DESC');
    } else {
      setSortField(field);
      setSortDir('DESC');
    }
    setPage(1);
  }, [sortField]);

  const applySearch = useCallback(() => {
    setAppliedQ(q);
    setPage(1);
  }, [q]);

  const handlePlanChange = useCallback((id) => {
    setPlanId(id);
    setPage(1);
  }, []);

  return (
    <div className="cv2-page">
      <ClientsToolbar
        active={active}
        plans={plans}
        planId={planId}
        onPlanChange={handlePlanChange}
        q={q}
        onQChange={setQ}
        onApplySearch={applySearch}
        total={meta.total}
      />

      <ClientsTable
        rows={rows}
        loading={loading}
        error={error}
        sortField={sortField}
        sortDir={sortDir}
        onSort={onSort}
        onRowClick={(id) => setSelectedClientId(id)}
      />

      <ClientsPagination
        page={page}
        totalPages={meta.total_pages || 0}
        onChange={setPage}
      />

      {selectedClientId && (
        <ClientShow
          clientId={selectedClientId}
          onClose={() => setSelectedClientId(null)}
        />
      )}
    </div>
  );
}

function ClientsPagination({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;
  return (
    <div className="cv2-pagination">
      <button disabled={page <= 1} onClick={() => onChange(page - 1)}>‹ Anterior</button>
      <span>Página <strong>{page}</strong> de {totalPages}</span>
      <button disabled={page >= totalPages} onClick={() => onChange(page + 1)}>Siguiente ›</button>
    </div>
  );
}
